import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";

import LOGO from "../../../assets/logo.webp";
import { routes } from "../../../routes";
import { COLORS, palette } from "../../../theme/theme-options";
import { SIDEBAR_COLLAPSED_WIDTH_PX } from "../../../constants";

const isActive = (pathname: string, href?: string) => {
  if (!href) {
    return false;
  }
  if (href === "/") {
    return pathname === "/";
  }
  return pathname.startsWith(href);
};

const normalizeLabel = (value: string): string => value.trim().toLowerCase();

const MobileSideNav: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [overlayOpen, setOverlayOpen] = React.useState(false);
  const [openMacroId, setOpenMacroId] = React.useState<string | null>(null);
  const [openSubId, setOpenSubId] = React.useState<string | null>(null);

  const handleNavigate = (href: string) => {
    navigate(href);
    setOverlayOpen(false);
    setOpenMacroId(null);
    setOpenSubId(null);
  };

  const openOverlay = (macroId?: string) => {
    setOverlayOpen(true);
    if (macroId) {
      setOpenMacroId(macroId);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: SIDEBAR_COLLAPSED_WIDTH_PX,
          height: "100vh",
          position: "sticky",
          top: 0,
          backgroundColor: palette.sidebar.bg,
          backdropFilter: "blur(16px)",
          borderRight: `1px solid ${palette.sidebar.border}`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            padding: "14px 8px 10px 8px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ButtonBase
            onClick={() => openOverlay()}
            sx={{ display: "grid", placeItems: "center", borderRadius: 2 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(135deg, ${alpha(COLORS.primary[500], 0.18)}, ${alpha(
                  COLORS.primary[600],
                  0.12,
                )})`,
                border: `1px solid ${alpha(COLORS.primary[400], 0.22)}`,
                overflow: "hidden",
              }}
            >
              <img src={LOGO} width={30} alt="Logo" />
            </Box>
          </ButtonBase>
        </Box>

        <Divider sx={{ borderColor: palette.sidebar.border }} />

        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
          {routes.map((item) => {
            const active = isActive(pathname, item.href);
            const hasSubs = item.subs.length > 0;

            return (
              <ButtonBase
                key={item.href}
                onClick={() => {
                  if (hasSubs) {
                    openOverlay(item.href);
                    return;
                  }
                  handleNavigate(item.href);
                }}
                sx={{ width: "100%", justifyContent: "left" }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    minHeight: 40,
                    padding: "8px 0px",
                    color: active ? palette.sidebar.textStrong : palette.sidebar.text,
                    backgroundColor: active
                      ? alpha(COLORS.primary[500], 0.12)
                      : "transparent",
                    transition: "all 160ms ease",
                    "&:hover": {
                      backgroundColor: alpha(COLORS.grey[800], 0.35),
                      color: palette.sidebar.textStrong,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      backgroundColor: active ? palette.sidebar.accent : "transparent",
                    }}
                  />
                  <Box sx={{ width: 22, display: "grid", placeItems: "center" }}>
                    {item.icon}
                  </Box>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>

        <Box sx={{ flex: 1 }} />

        <Divider sx={{ borderColor: palette.sidebar.border }} />
        <Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
          <IconButton
            size="small"
            onClick={() => openOverlay()}
            sx={{
              color: alpha(COLORS.grey[300], 0.85),
              border: `1px solid ${alpha(COLORS.grey[700], 0.7)}`,
              borderRadius: 2,
              width: 30,
              height: 30,
              "&:hover": {
                backgroundColor: alpha(COLORS.grey[800], 0.35),
                color: COLORS.grey[100],
              },
            }}
          >
            <MenuRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={overlayOpen}
        onClose={() => setOverlayOpen(false)}
        PaperProps={{
          sx: {
            width: "100vw",
            maxWidth: "100vw",
            backgroundColor: palette.sidebar.bg,
            borderRight: `1px solid ${palette.sidebar.border}`,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(135deg, ${alpha(COLORS.primary[500], 0.18)}, ${alpha(
                  COLORS.primary[600],
                  0.12,
                )})`,
                border: `1px solid ${alpha(COLORS.primary[400], 0.22)}`,
                overflow: "hidden",
              }}
            >
              <img src={LOGO} width={26} alt="Logo" />
            </Box>
            <Typography
              sx={{
                color: palette.sidebar.textStrong,
                fontWeight: 800,
                fontSize: 22,
                lineHeight: 1,
              }}
            >
              Pure IT
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOverlayOpen(false)}
            sx={{ color: palette.sidebar.textStrong }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: palette.sidebar.border }} />

        <Box sx={{ flex: 1, overflowY: "auto", p: 1.2 }}>
          {routes.map((item) => {
            const active = isActive(pathname, item.href);
            const hasSubs = item.subs.length > 0;

            if (!hasSubs) {
              return (
                <ButtonBase
                  key={item.href}
                  onClick={() => handleNavigate(item.href)}
                  sx={{
                    width: "100%",
                    justifyContent: "flex-start",
                    borderRadius: 2,
                    px: 1.25,
                    py: 1.2,
                    mb: 0.6,
                    textAlign: "left",
                    color: active ? palette.sidebar.textStrong : palette.sidebar.text,
                    backgroundColor: active
                      ? alpha(COLORS.primary[500], 0.14)
                      : "transparent",
                  }}
                >
                  <Box sx={{ display: "grid", placeItems: "center", mr: 1.2 }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, textAlign: "left" }}>
                    {item.label}
                  </Typography>
                </ButtonBase>
              );
            }

            return (
              <Accordion
                key={item.href}
                disableGutters
                expanded={openMacroId === item.href}
                onChange={(_, expanded) => {
                  setOpenMacroId(expanded ? item.href : null);
                  if (!expanded) {
                    setOpenSubId(null);
                  }
                }}
                sx={{
                  mb: 0.75,
                  borderRadius: "10px !important",
                  overflow: "hidden",
                  border: `1px solid ${alpha(COLORS.grey[700], 0.5)}`,
                  backgroundColor: "transparent",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreRoundedIcon sx={{ color: palette.sidebar.textStrong }} />
                  }
                  sx={{
                    minHeight: 44,
                    px: 1.2,
                    backgroundColor: active
                      ? alpha(COLORS.primary[500], 0.14)
                      : alpha(COLORS.grey[800], 0.2),
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      alignItems: "center",
                      gap: 1.1,
                      margin: 0,
                      textAlign: "left",
                    },
                  }}
                >
                  <Box sx={{ display: "grid", placeItems: "center" }}>{item.icon}</Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: palette.sidebar.textStrong,
                      textAlign: "left",
                    }}
                  >
                    {item.label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0.8 }}>
                  {item.subs.map((group) => {
                    const groupKey = `${item.href}#${group.main.href}`;
                    const isDuplicateGroupTitle =
                      normalizeLabel(group.main.label) === normalizeLabel(item.label);

                    if (isDuplicateGroupTitle) {
                      return (
                        <Box key={groupKey} sx={{ mb: 0.6 }}>
                          {group.children.map((child) => {
                            const childActive = isActive(pathname, child.href);
                            return (
                              <ButtonBase
                                key={child.href}
                                onClick={() => handleNavigate(child.href)}
                                sx={{
                                  width: "100%",
                                  justifyContent: "flex-start",
                                  borderRadius: 1.2,
                                  px: 1.1,
                                  py: 0.8,
                                  mb: 0.45,
                                  textAlign: "left",
                                  color: childActive
                                    ? palette.sidebar.textStrong
                                    : palette.sidebar.text,
                                  backgroundColor: childActive
                                    ? alpha(COLORS.primary[500], 0.14)
                                    : "transparent",
                                  "&:hover": {
                                    backgroundColor: alpha(COLORS.primary[500], 0.16),
                                  },
                                }}
                              >
                                <Typography sx={{ fontSize: 14, fontWeight: 700, textAlign: "left" }}>
                                  {child.label}
                                </Typography>
                              </ButtonBase>
                            );
                          })}
                        </Box>
                      );
                    }

                    return (
                      <Accordion
                        key={groupKey}
                        disableGutters
                        expanded={openSubId === groupKey}
                        onChange={(_, expanded) => {
                          setOpenSubId(expanded ? groupKey : null);
                        }}
                        sx={{
                          mb: 0.6,
                          borderRadius: "8px !important",
                          overflow: "hidden",
                          border: `1px solid ${alpha(COLORS.grey[700], 0.45)}`,
                          backgroundColor: alpha(COLORS.grey[900], 0.45),
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreRoundedIcon sx={{ color: palette.sidebar.textStrong }} />
                          }
                          sx={{
                            minHeight: 40,
                            px: 1,
                            backgroundColor: alpha(COLORS.grey[800], 0.28),
                            "& .MuiAccordionSummary-content": { m: 0, textAlign: "left" },
                          }}
                        >
                          <Typography
                            sx={{ color: palette.sidebar.textStrong, fontWeight: 700, textAlign: "left" }}
                          >
                            {group.main.label}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0.65 }}>
                          {group.children.map((child) => {
                            const childActive = isActive(pathname, child.href);
                            return (
                              <ButtonBase
                                key={child.href}
                                onClick={() => handleNavigate(child.href)}
                                sx={{
                                  width: "100%",
                                  justifyContent: "flex-start",
                                  borderRadius: 1.2,
                                  px: 1.1,
                                  py: 0.8,
                                  mb: 0.45,
                                  textAlign: "left",
                                  color: childActive
                                    ? palette.sidebar.textStrong
                                    : palette.sidebar.text,
                                  backgroundColor: childActive
                                    ? alpha(COLORS.primary[500], 0.14)
                                    : "transparent",
                                  "&:hover": {
                                    backgroundColor: alpha(COLORS.primary[500], 0.16),
                                  },
                                }}
                              >
                                <Typography sx={{ fontSize: 14, fontWeight: 700, textAlign: "left" }}>
                                  {child.label}
                                </Typography>
                              </ButtonBase>
                            );
                          })}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Drawer>
    </>
  );
};

export default MobileSideNav;
